import { Component, OnInit } from '@angular/core';
import { IUser } from 'projects/common/src/app/interfaces';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableExtraParams } from 'projects/common/src/app/interfaces/table/table-extra-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { CacheService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { ICheckFilter } from 'src/app/modules/core/interfaces/check-filter';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { IMarineLoop } from '../../../interfaces/marine-loop';
import { ReportMarineService } from '../../../services/report-marine.service';

@Component({
  selector: 'app-marine-shell',
  templateUrl: './marine-shell.component.html',
  styleUrls: ['./marine-shell.component.scss'],
})
export class MarineShellComponent implements OnInit {
  public message: string;
  public hidden: boolean;
  public reportData: IMarineLoop[];
  public showFilter: ICheckFilter;
  public tableConfig: ITableConfig;
  public tableByMachine: ITableConfig;
  public tableByShift: ITableConfig;
  public isDisabledFilterBtn: boolean;
  public canDownload: boolean;
  public hasData: boolean;
  public systemError: string;
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  public params: any;

  constructor(
    private tableConfigService: TableConfigService,
    private languageService: LanguageService,
    private cacheService: CacheService,
    private reportMarineService: ReportMarineService,
    private layoutConfigService: LayoutConfigService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
  ) {
    this.hidden = true;
    this.hasData = false;
    this.isDisabledFilterBtn = false;
    this.disabledExcelBtn = false;

    const allMachines = this.cacheService.session('machineCollection') as IMachineContext[];
    const selected = allMachines.find((v) => v.code === 'SCOP');

    this.filterConfig = {
      dates: { required: true },
      shift: { required: true },
      machineType: { required: true, uniqueSelection: selected },
      machineList: { required: true, multiselect: true },
      cleanButton: true,
    };

    this.canDownload = false;
  }

  /**
   * Init component
   */
  public ngOnInit(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'report-marine',
      imageKey: 'report',
      imageType: 'section',
    };
    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to create or modify table data
   *
   * @param $event Filters incoming
   */
  public createTableData($event: IOutputFilter): void {
    this.filterConfig.isLoading = true;
    this.canDownload = false;
    const user = this.cacheService.local('session').user as IUser;

    this.params = {
      startDate: $event.startDate,
      endDate: $event.endDate,
      shiftId: $event.shiftId,
      machineTypeList: $event.machineTypeId,
      machineList: $event.machineId,
      companyId: user.company.id,
    };

    this.reportMarineService.getMarineReport(this.params).then(
      (response: any) => {
        if (
          response &&
          response.data.loopsDetailByDate.length &&
          response.data.totalLoopsByMachine.length &&
          response.data.totalLoopsByShift.length
        ) {
          this.hidden = false;
          this.canDownload = true;
          this.hasData = true;
          this.setTableDetailsConfig(response.data.loopsDetailByDate);
          this.setTableByMachine(response.data.totalLoopsByMachine);
          this.reportData = response.data.totalLoopsByShift;
          this.message = '';
          this.isDisabledFilterBtn = false;
        } else {
          this.hasData = false;
          this.canDownload = false;
          this.isDisabledFilterBtn = true;
          this.tableConfig = null;
          this.tableByMachine = null;
          this.tableByShift = null;
          this.message = this.languageService.message('m034');
        }
        this.systemError = '';
        this.filterConfig.isLoading = false;
      },
      ({ name }) => {
        this.hasData = false;
        this.canDownload = false;
        this.systemError = name === 'TimeoutError' ? 'm014' : 'm017';
        this.filterConfig.isLoading = false;
      },
    );
    const filters = {
      reports: 'report-marine',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Function to download excel from endpoint recived file
   */
  public downloadExcel(): void {
    const selectRangeDate = {
      startDate: this.params.startDate,
      endDate: this.params.endDate,
    };
    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this.reportMarineService.getMarineReportExcel(this.params).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'loops-report', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }

  /**
   * Function to set a tableDetails by config
   */
  private setTableDetailsConfig(tableData: any[]): void {
    const headerParams: ITableHeaderParams = {
      data: tableData,
      sortables: ['machineName', 'operator', 'startZone', 'startedAt', 'endZone', 'finishedAt', 'difference', 'shiftName'],
      replaceColumnName: [{ prevName: 'machineName', newName: 'machineType' }],
    };

    const extraParams: ITableExtraParams = {
      disableBackground: true,
    };

    const datasetParams: ITableDataParams = {
      tableData,
      dateConfig: [
        { field: 'startedAt', format: 'date-time' },
        { field: 'finishedAt', format: 'date-time' },
      ],
      colorConfig: [{ field: 'machineName', isBold: true }],
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableConfig = null;
    setTimeout(() => (this.tableConfig = { header, dataset, extraParams }), 0);
  }

  /**
   * Function to set a tableByMachine by config
   */
  private setTableByMachine(tableData: any[]): void {
    const excludeColumns = ['machineId'];

    const extraParams: ITableExtraParams = {
      disableBackground: true,
      disableFooter: true,
      minimalRows: 6,
      rowsHeight: 6,
      quantityPerPage: tableData.length < 10 ? 10 : tableData.length,
    };

    const headerParams: ITableHeaderParams = {
      excludeColumns,
      data: tableData,
      replaceColumnName: [{ prevName: 'machineName', newName: 'machineType' }],
    };
    const datasetParams: ITableDataParams = {
      tableData,
      excludeColumns,
      colorConfig: [{ field: 'machineName', isBold: true }],
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableByMachine = null;
    setTimeout(() => (this.tableByMachine = { header, dataset, extraParams }), 0);
  }
}
